import { supabase } from '../lib/supabase';

// Dodo Payments window type
declare global {
  interface Window {
    DodoPayments?: {
      Initialize: (config: { mode: string; apiKey: string }) => void;
      checkout: (config: {
        product_id: string;
        customer_email: string;
        customer_id: string;
        success_url: string;
        cancel_url: string;
        onSuccess: (data: any) => void;
        onCancel: () => void;
        onError: (error: any) => void;
      }) => void;
    };
  }
}

// Dodo Payments configuration
const DODO_API_KEY = import.meta.env.VITE_DODO_API_KEY;
const DODO_BASE_URL = import.meta.env.MODE === 'production' 
  ? 'https://live.dodopayments.com' 
  : 'https://test.dodopayments.com';

export const dodoService = {
  // Create checkout session via our secure server-side function
  async createCheckoutSession(productId: string, userId: string, email: string) {
    try {
      console.log('Creating secure checkout session...', { productId, userId, email });
      
      const response = await supabase.functions.invoke('create-dodo-checkout', {
        body: {
          productId,
          userId,
          email
        }
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to create checkout session');
      }

      return response.data;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  },

  // Redirect to secure checkout
  async redirectToCheckout(productId: string, userId: string, email: string) {
    try {
      console.log('Starting secure checkout...', { productId, userId, email });
      
      const session = await this.createCheckoutSession(productId, userId, email);
      
      if (session.checkout_url) {
        console.log('Redirecting to secure checkout URL:', session.checkout_url);
        window.location.href = session.checkout_url;
      } else {
        throw new Error('No checkout URL returned from server');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      throw error;
    }
  },

  // Create or get existing customer
  async createOrGetCustomer(email: string, userId: string) {
    try {
      // Check if customer exists in our database
      const { data: subscriptions } = await supabase
        .from('subscriptions')
        .select('dodo_customer_id')
        .eq('user_id', userId);

      if (subscriptions && subscriptions.length > 0 && subscriptions[0]?.dodo_customer_id) {
        // Return existing customer
        return { customer_id: subscriptions[0].dodo_customer_id };
      }

      // Create new customer in Dodo Payments
      const response = await fetch(`${DODO_BASE_URL}/customers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${DODO_API_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          name: email.split('@')[0] // Use email prefix as name
        })
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || 'Failed to create customer');
      }

      const customer = await response.json();

      // Store customer ID in our database
      await supabase
        .from('subscriptions')
        .upsert({
          user_id: userId,
          dodo_customer_id: customer.customer_id,
          status: 'trialing',
          created_at: new Date().toISOString()
        });

      return customer;
    } catch (error) {
      console.error('Error creating/getting customer:', error);
      throw error;
    }
  },

  // Get subscription status
  async getSubscription(subscriptionId: string) {
    try {
      const response = await fetch(`${DODO_BASE_URL}/subscriptions/${subscriptionId}`, {
        headers: {
          'Authorization': `Bearer ${DODO_API_KEY}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get subscription');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting subscription:', error);
      throw error;
    }
  },

  // Cancel subscription
  async cancelSubscription(subscriptionId: string) {
    try {
      const response = await fetch(`${DODO_BASE_URL}/subscriptions/${subscriptionId}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${DODO_API_KEY}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to cancel subscription');
      }

      return await response.json();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  },


}; 